/**
 * O modelo local como um contato do messenger.
 *
 * A ideia inteira cabe numa frase: um GGUF no aparelho vira um endereço de
 * conversa, `local:<arquivo>`, e daí em diante ele usa o mesmo caminho que uma
 * pessoa — mesma lista, mesma bolha, mesmo histórico, mesmo "digitando…".
 *
 * O que ISTO resolve, e que a aba de agentes locais não resolvia: lá o modelo
 * respondia num campo de teste, fora da conversa. Dava para provar que o motor
 * funcionava, não para usar o modelo. A diferença entre as duas coisas é a
 * diferença entre uma bancada e um produto.
 *
 * Uma regra atravessa o arquivo: **nada daqui toca a rede**. A conversa com o
 * modelo é gravada no IndexedDB e morre no aparelho — nenhum `WireSend` sai,
 * nenhum servidor vê o texto. É o que torna honesto oferecer o modelo dentro de
 * uma conversa privada; se a inferência fosse remota, seria o contrário.
 */

import * as llmNativo from "./llm-nativo.js";
import type { ChatMessage } from "./types.js";

/** Prefixo do endereço. `g:` já marcava grupo; `local:` marca modelo. */
export const PREFIXO = "local:";

export function ehContatoLocal(contato: string): boolean {
  return contato.startsWith(PREFIXO);
}

export function contatoDoArquivo(arquivo: string): string {
  return PREFIXO + arquivo;
}

export function arquivoDoContato(contato: string): string {
  return contato.slice(PREFIXO.length);
}

/** Nome de exibição a partir do arquivo.
 *
 *  `gemma-3-1b-it-Q4_K_M.gguf` vira `Gemma 3 1b it`. A quantização sai do nome
 *  porque ela responde "como foi comprimido", que não é o que se procura numa
 *  lista de conversas — e quem quiser o arquivo exato o vê na tela do modelo. */
export function nomeDoModelo(arquivo: string): string {
  const semExt = arquivo.replace(/\.gguf$/i, "");
  // Corta a partir da marca de quantização até o FIM do nome.
  //
  // A primeira versão removia só o pedaço casado, e `gemma-3-1b-it-Q4_K_M`
  // virava "Gemma 3 1b it K M" — a tela ficou com um nome que ninguém
  // reconhece e que ainda por cima não cabia no cabeçalho. Tudo que vem
  // depois do `Q4` descreve a compressão, e nada disso ajuda a escolher com
  // quem falar.
  const semQuant = semExt.replace(/[-_.](Q\d+.*|IQ\d+.*|f16|f32|bf16)$/i, "");
  const palavras = semQuant.replace(/[-_]+/g, " ").trim();
  return palavras.charAt(0).toUpperCase() + palavras.slice(1);
}

/** Os modelos que dá para conversar, já como endereço de contato.
 *
 *  Reaproveita `papelDoModelo` em vez de reimplementar a regra: foi ela que
 *  aprendeu a recusar projetor e rascunho, e uma segunda cópia da regra é uma
 *  cópia que vai divergir. Um `mmproj` na lista de conversas devolveria
 *  "unsupported model architecture" ao primeiro "oi". */
export function modelosParaConversa(): Array<{ contato: string; arquivo: string; nome: string }> {
  return llmNativo
    .modelos()
    .filter((m) => llmNativo.papelDoModelo(m) === "conversa")
    .map((m) => ({
      contato: contatoDoArquivo(m.nome),
      arquivo: m.nome,
      nome: nomeDoModelo(m.nome),
    }));
}

/** Qual modelo está de fato na memória do motor.
 *
 *  O estado nativo diz `carregado: true/false`, mas não diz QUAL — e com dois
 *  modelos de conversa no aparelho isso não basta: mandar uma mensagem para o
 *  segundo enquanto o primeiro está carregado geraria a resposta do modelo
 *  errado, sem erro nenhum aparecendo. Por isso a lembrança mora aqui. */
export function modeloNaMemoria(): string | null {
  const e = llmNativo.estado();
  if (!e || !e.carregado) return null;
  return e.modelo ?? null;
}

/** O sistema tirou o modelo da memória desde a última pergunta?
 *
 *  Serve para a tela explicar a espera. Sem isso ela só sabe dizer "carregando
 *  o modelo" pela segunda vez, e quem lê conclui que o app está com defeito —
 *  foi exatamente o relato que chegou do aparelho. */
let soltouPorMemoria = false;

if (typeof window !== "undefined") {
  window.addEventListener("wuwei-llm", (ev) => {
    const d = (ev as CustomEvent).detail as { tipo?: string; motivo?: string };
    if (d?.tipo === "descarregado" && d?.motivo === "memoria") soltouPorMemoria = true;
  });
}

/** Frase para a fase de carga, já sabendo se houve despejo por memória. */
export function textoDaCarga(): string {
  if (soltouPorMemoria) {
    soltouPorMemoria = false;
    return "o sistema pediu a memória de volta — carregando o modelo de novo…";
  }
  return "carregando o modelo…";
}

/** O que o modelo é dentro desta conversa.
 *
 *  Curto de propósito. O alvo é um modelo de 1B rodando num celular: instrução
 *  longa come contexto que faria falta para a conversa, e modelo pequeno
 *  obedece pior quanto mais se pede. */
const INSTRUCAO =
  "Você é um assistente que roda dentro do celular de quem fala com você, sem internet. " +
  "Responda em português do Brasil, de forma curta e direta. " +
  "Se não souber, diga que não sabe.";

/**
 * Quanto texto do histórico cabe no prompt, em caracteres.
 *
 * Contar MENSAGENS não diz nada: uma mensagem tem três letras ou três mil, e
 * doze delas tanto podem desperdiçar a janela quanto estourá-la. Num teste
 * real, oito mensagens longas encheram o contexto antes de o modelo chegar ao
 * começo da conversa, e ele resumiu só o fim — com aparência de resumo do todo.
 *
 * O orçamento sai do próprio motor: ele informa o contexto do modelo carregado,
 * daí se reserva o espaço da resposta e da instrução, e o que sobra é do
 * histórico. Assim um modelo com janela maior é aproveitado sozinho, sem
 * ninguém mexer num número aqui.
 *
 * Quatro caracteres por token é regra grosseira e conservadora para português —
 * erra para menos, que é o lado seguro: sobra contexto em vez de faltar.
 */
const CHARS_POR_TOKEN = 4;
const TOKENS_DA_RESPOSTA = 512;
const TOKENS_DA_INSTRUCAO = 120;

function orcamentoDeChars(): number {
  const contexto = llmNativo.estado()?.contexto || 2048;
  const paraHistorico = contexto - TOKENS_DA_RESPOSTA - TOKENS_DA_INSTRUCAO;
  return Math.max(400, paraHistorico * CHARS_POR_TOKEN);
}

/** Quanto do orçamento sobra ocupado logo depois de um corte.
 *
 *  Isto existe por causa do cache do motor. Ele reaproveita a ingestão
 *  comparando o prompt novo com o que já está na memória: acha o pedaço comum
 *  no COMEÇO e só processa o que veio depois. O header do llama.rn diz isso
 *  para modelos de atenção pura — "seq_rm already reuses the prefix for free".
 *
 *  A primeira versão daqui matava esse reaproveitamento. Ela pegava da mensagem
 *  mais recente para trás até encher: cada mensagem nova empurrava uma velha
 *  para fora, o começo do prompt andava um passo, e o pedaço comum virava quase
 *  nada. Uma janela que desliza a cada mensagem obriga o modelo a reingerir a
 *  conversa inteira toda vez — é o pior formato possível para cache.
 *
 *  Cortando em degraus, o começo fica parado entre um corte e outro, e nesse
 *  intervalo só a mensagem recém-escrita precisa ser ingerida. Sessenta por
 *  cento é a folga: corta-se um bloco de uma vez e ainda cabem várias trocas
 *  antes do próximo corte. Cortar só o necessário devolveria o problema —
 *  seria um degrau por mensagem, que é a janela deslizante de novo. */
const ALVO_APOS_CORTE = 0.6;

/** O "quem falou" de cada linha também ocupa lugar no prompt. */
function custoDe(m: ChatMessage): number {
  return m.text.length + 12;
}

/** A janela do histórico, com começo estável.
 *
 *  Percorre a conversa do início para o fim mantendo um começo que só anda
 *  quando o orçamento estoura — e, quando anda, anda um bloco. É função pura
 *  da conversa inteira, então duas chamadas seguidas com a mesma conversa dão
 *  o mesmo começo, que é exatamente o que o cache precisa. */
function janelaEstavel(mensagens: ChatMessage[], orcamento: number): ChatMessage[] {
  const uteis = mensagens.filter((m) => !m.deleted && m.text);
  let inicio = 0;
  let usado = 0;
  for (let i = 0; i < uteis.length; i++) {
    usado += custoDe(uteis[i]!);
    if (usado <= orcamento) continue;
    // `inicio < i` segura a mensagem atual: a última nunca é descartada, nem
    // quando ela sozinha é maior que o orçamento inteiro.
    while (usado > orcamento * ALVO_APOS_CORTE && inicio < i) {
      usado -= custoDe(uteis[inicio]!);
      inicio++;
    }
  }
  return uteis.slice(inicio);
}

/** Quantas mensagens entram quando o pedido é resumir, e com que tamanho.
 *
 *  Larga e rasa, ao contrário do padrão. Resumir com oito mensagens inteiras
 *  produziu, num teste real, um resumo do FIM da conversa apresentado como
 *  resumo da conversa: as mensagens longas encheram os 2048 tokens antes de o
 *  modelo chegar ao começo, e ele resumiu o que coube. Vinte mensagens cortadas
 *  em 200 caracteres cobrem o arco inteiro e ocupam menos espaço do que oito
 *  inteiras — para resumo, saber que assunto existiu vale mais que a frase
 *  exata. */
export function historicoParaResumo(mensagens: ChatMessage[]): llmNativo.Mensagem[] {
  // Para resumir, mensagem longa é cortada antes de o orçamento acabar: saber
  // QUE assunto existiu vale mais que a frase exata, e cortar cada uma deixa
  // caber trechos do começo da conversa em vez de só do fim.
  const orcamento = orcamentoDeChars();
  // O corte sai do NÚMERO de mensagens a cobrir, não de uma constante: para
  // resumir, o que importa é alcançar o começo da conversa, e um corte fixo
  // gasta o orçamento nas primeiras que encontra — que são as mais recentes.
  // Com um teto de mensagens, cada uma recebe a fatia que permite todas caberem.
  const COBERTURA = 40;
  const quantas = Math.min(
    COBERTURA,
    mensagens.filter((m) => !m.deleted && m.text).length || 1,
  );
  // 90% do orçamento, e não 100%: o "eu:"/"ele:" de cada linha também ocupa
  // lugar, e estourar por pouco descarta justamente a mensagem mais antiga —
  // a única que o resumo não pode perder.
  const corte = Math.max(60, Math.floor((orcamento * 0.9) / quantas) - 12);
  const encolhidas = mensagens
    .filter((m) => !m.deleted && m.text)
    .slice(-COBERTURA)
    .map((m) => ({
      ...m,
      text: m.text.length > corte ? m.text.slice(0, corte) + "…" : m.text,
    }));
  // Sem passar pelo orçamento de novo: o corte acima já garante o tamanho, e
  // recortar duas vezes voltaria a comer o começo da conversa.
  const ultimas = encolhidas.map((m) => ({
    quem: m.direction === "out" ? "eu" : "ele",
    texto: m.text,
  }));
  // Vai como UMA mensagem, e não como turnos alternados: pedir resumo de um
  // diálogo encenado faz modelo pequeno continuar o diálogo em vez de resumir.
  const transcricao = ultimas.map((m) => `${m.quem}: ${m.texto}`).join("\n");
  return [
    {
      role: "system",
      content:
        "Você resume conversas. Responda em português do Brasil, em no máximo três frases, " +
        "dizendo os assuntos tratados do começo ao fim. Não continue a conversa.",
    },
    { role: "user", content: `Resuma esta conversa:\n\n${transcricao}` },
  ];
}

/** Quanto do orçamento fica reservado ao começo da conversa.
 *
 *  Uma janela que só leva as mensagens recentes esquece o que foi combinado no
 *  início — e é no início que costuma estar o que vale para o resto: um nome,
 *  uma decisão, uma instrução. Vinte por cento é pouco para atrapalhar a
 *  conversa recente e suficiente para caber o acordo.
 *
 *  Isto não se confunde com a âncora de atenção do StreamingLLM (Xiao et al.,
 *  ICLR 2024). Lá se prendem os primeiros TOKENS, e o que se preserva é a
 *  coerência do modelo quando o contexto estoura; o conteúdo descartado
 *  continua perdido. Aqui se preservam as primeiras MENSAGENS, e o que se
 *  preserva é o conteúdo. São dois problemas diferentes, e este é o que
 *  aparece na conversa de quem usa.
 *
 *  De quebra, ajuda o cache: o começo do prompt passa a ser sempre o mesmo
 *  texto, e prefixo estável é o que o motor reaproveita. */
const FRACAO_DA_ANCORA = 0.2;

/** As primeiras mensagens da conversa, até encher a fatia reservada. */
function ancoraDaConversa(mensagens: ChatMessage[], orcamento: number): ChatMessage[] {
  const escolhidas: ChatMessage[] = [];
  let usado = 0;
  for (const m of mensagens) {
    if (m.deleted || !m.text) continue;
    const custo = custoDe(m);
    if (usado + custo > orcamento) {
      // A primeira mensagem sozinha pode ser maior que a fatia inteira. Deixá-la
      // entrar por ser a primeira estouraria o orçamento do prompt e comeria o
      // espaço da conversa recente; deixá-la de fora perderia justamente o
      // trecho que se quis preservar. Entra cortada — para um acordo, o começo
      // costuma bastar.
      if (escolhidas.length === 0) {
        const cabe = Math.max(0, Math.floor(orcamento) - 12);
        if (cabe > 40) escolhidas.push({ ...m, text: m.text.slice(0, cabe) + "…" });
      }
      break;
    }
    escolhidas.push(m);
    usado += custo;
  }
  return escolhidas;
}

export function historicoParaPrompt(mensagens: ChatMessage[]): llmNativo.Mensagem[] {
  const orcamento = orcamentoDeChars();
  const ancora = ancoraDaConversa(mensagens, orcamento * FRACAO_DA_ANCORA);
  const ultimas = janelaEstavel(mensagens, orcamento * (1 - FRACAO_DA_ANCORA));

  // Conversa curta: a âncora e a janela são a mesma coisa, e mandar as duas
  // faria o modelo ler tudo em dobro — o que além de desperdício confunde,
  // porque uma pergunta repetida parece uma pergunta feita duas vezes.
  const idsDaJanela = new Set(ultimas.map((m) => m.id));
  const soAncora = ancora.filter((m) => !idsDaJanela.has(m.id));

  const linhas = (m: ChatMessage): llmNativo.Mensagem => ({
    role: m.direction === "out" ? "user" : "assistant",
    content: m.text,
  });

  return alternando([
    { role: "system", content: INSTRUCAO },
    ...soAncora.map(linhas),
    // A marca do corte existe para o modelo não costurar o começo da conversa
    // com o fim como se fossem contíguos — sem ela, ele responde a perguntas
    // que ninguém fez, emendando duas partes distantes.
    ...(soAncora.length && soAncora.length + ultimas.length < mensagens.length
      ? [{ role: "user", content: "(trecho do meio da conversa omitido)" } as llmNativo.Mensagem]
      : []),
    ...ultimas.map(linhas),
  ]);
}

/** Junta falas seguidas da mesma pessoa numa só.
 *
 *  O template do Gemma — e o de outras famílias — EXIGE alternância entre
 *  pergunta e resposta, e recusa a conversa inteira quando encontra duas falas
 *  seguidas do mesmo lado. A recusa não chega como aviso: o motor devolve
 *  prompt vazio e a tela diz que o modelo não tem template de conversa.
 *
 *  Isso acontece sozinho, sem ninguém errar: uma conversa que começa com duas
 *  mensagens da pessoa, uma janela que corta bem no meio de um par, ou a marca
 *  de trecho omitido caindo depois de uma fala dela. Garantir a alternância
 *  aqui é mais barato que fazer cada montagem se preocupar com isso — e foi
 *  assim que a marca de corte, publicada como melhoria, quebrou a conversa. */
function alternando(mensagens: llmNativo.Mensagem[]): llmNativo.Mensagem[] {
  const saida: llmNativo.Mensagem[] = [];
  for (const m of mensagens) {
    const ultima = saida[saida.length - 1];
    // O papel de sistema fica de fora: ele não participa da alternância, e
    // juntá-lo à primeira pergunta apagaria a distinção entre instrução e fala.
    if (ultima && ultima.role === m.role && m.role !== "system") {
      ultima.content = `${ultima.content}\n\n${m.content}`;
      continue;
    }
    saida.push({ ...m });
  }
  return saida;
}

export interface Andamento {
  /** Em que ponto a resposta está.
   *
   *  Três fases, e não duas, porque a tela dizia "carregando o modelo" para as
   *  duas primeiras — inclusive quando o modelo já estava na memória e o que
   *  demorava era ele ler a conversa. Quem esperava concluía que o modelo
   *  recarregava a cada pergunta, e passou a duvidar da correção que já estava
   *  funcionando. Uma frase errada custou meio dia de diagnóstico. */
  fase: "carregando" | "restaurando" | "lendo" | "gerando";
  texto: string;
}

/** A frase de cada fase, já contando o despejo por memória. */
export function textoDaFase(fase: Andamento["fase"], texto = ""): string {
  if (fase === "restaurando") return "carregando a conversa do disco…";
  // O número, quando existe, é o que separa "trouxe quase tudo pronto" de
  // "está relendo tudo" — e era a diferença que ninguém conseguia ver na tela.
  if (fase === "lendo") {
    if (!texto) return "lendo a conversa…";
    // "512/1400" é progresso em curso; um número solto é o que veio do disco.
    // Relógio ("1min 20s") é espera em curso; número solto é o que veio pronto.
    if (/[a-z]/.test(texto)) return `lendo a conversa… ${texto}`;
    return `${texto} tokens vieram do disco — lendo o que falta…`;
  }
  return textoDaCarga();
}

/**
 * Responde a uma conversa local, transmitindo os pedaços conforme saem.
 *
 * Carrega o modelo sob demanda, e não no arranque do app: são centenas de MB
 * que só fazem sentido para quem realmente abriu a conversa. O preço é a espera
 * da primeira mensagem — daí a fase `carregando` existir e a tela ter o que
 * mostrar. Silêncio de trinta segundos é indistinguível de travamento.
 */
export async function responder(
  arquivo: string,
  historico: ChatMessage[],
  aoAndamento: (a: Andamento) => void,
  modo: "conversa" | "resumo" = "conversa",
  // A conversa a que este pedido pertence. Sem ela não há como achar o estado
  // guardado em disco, e a geração relê tudo — que foi exatamente o que
  // aconteceu: o disco existia, estava gravado, e ninguém o consultava neste
  // caminho.
  contato = "",
): Promise<{ texto: string; porSegundo: number; prefill: string }> {
  if (!llmNativo.disponivel()) throw new Error(llmNativo.MOTIVO_SEM_PONTE);

  // Se o pré-aquecimento já está carregando este mesmo modelo, espera ele em
  // vez de começar outra carga — duas cargas do mesmo GGUF seriam o dobro do
  // tempo e o dobro da memória, pelo mesmo resultado.
  if (cargaEmCurso) {
    aoAndamento({ fase: "carregando", texto: "" });
    await esperarCarga();
  }

  if (modeloNaMemoria() !== arquivo) {
    aoAndamento({ fase: "carregando", texto: "" });
    // Se havia OUTRO modelo carregado, solta antes de pedir este: o motor
    // guarda um por vez, e pedir o segundo sem soltar o primeiro é a forma
    // mais rápida de estourar a memória num aparelho já no limite.
    if (modeloNaMemoria()) llmNativo.descarregar();
    await llmNativo.carregar(arquivo);
  }

  // Daqui em diante o modelo está na memória: o que demora é ele ler o que foi
  // mandado. Dizer "carregando" aqui seria repetir a mentira que fez o dono do
  // app achar que o modelo recarregava a cada pergunta.
  const mensagensDoPrompt =
    modo === "resumo" ? historicoParaResumo(historico) : historicoParaPrompt(historico);

  // Quantos tokens vieram prontos do disco. Guardado aqui porque a frase da
  // tela é reemitida depois da ingestão, e emitir vazio apagaria justamente o
  // número que responde "o disco serviu?".
  let vieramDoDisco = "";

  // Traz do disco antes de gerar.
  //
  // O motor compara o que você mandou com o que está na memória, acha o pedaço
  // em comum no começo e só processa o resto. Restaurar o estado guardado desta
  // conversa é o que faz esse pedaço em comum existir — sem isso, cada pergunta
  // relê a conversa inteira, mesmo com o arquivo gravado no aparelho.
  //
  // Silencioso quando falha: um estado que não serve custa uma leitura de
  // arquivo e nada mais; a geração seguinte funciona igual, só mais devagar.
  if (contato && llmNativo.ingestaoDisponivel()) {
    try {
      aoAndamento({ fase: "restaurando", texto: "" });
      // A troca de frase depende de um aviso que só o APK novo emite. Sem ele,
      // a tela ficaria dizendo "carregando do disco" durante a ingestão inteira
      // — e ler um arquivo não leva minutos, então a frase viraria mentira.
      // Passado o tempo de uma leitura de disco honesta, a frase troca sozinha.
      const trocarSozinho = setTimeout(() => {
        if (!vieramDoDisco) aoAndamento({ fase: "lendo", texto: "" });
      }, TEMPO_DE_UMA_LEITURA_DE_DISCO);
      // Tempo decorrido, e não progresso.
      //
      // A primeira versão perguntava ao motor quantos tokens já tinham passado.
      // Medido no emulador com conversa de 369 tokens: o contador ficou em ZERO
      // por quatro minutos e só recebeu o valor final quando tudo terminou — o
      // motor não o atualiza durante o trabalho. Uma barra parada em zero que
      // pula para o fim é pior que barra nenhuma.
      //
      // O relógio não finge saber o que não sabe. Ele diz que o aparelho está
      // trabalhando e há quanto tempo, que é o que a pessoa precisa para
      // decidir se espera ou desiste.
      const comecou = Date.now();
      const contador = setInterval(() => {
        aoAndamento({ fase: "lendo", texto: relogio(Date.now() - comecou) });
      }, 1000);
      try {
        await llmNativo.ingerir(mensagensDoPrompt, chaveDoEstado(contato, arquivo), () => {}, (n) => {
          vieramDoDisco = n > 0 ? String(n) : "";
          clearTimeout(trocarSozinho);
          aoAndamento({ fase: "lendo", texto: vieramDoDisco });
        });
      } finally {
        clearTimeout(trocarSozinho);
        clearInterval(contador);
      }
    } catch {
      // Segue sem o disco.
    }
  }

  aoAndamento({ fase: "lendo", texto: vieramDoDisco });

  let acumulado = "";
  const mensagens =
    modo === "resumo" ? historicoParaResumo(historico) : historicoParaPrompt(historico);
  const gerar = () =>
    llmNativo.gerarChat(mensagens, { maxTokens: 512, temperatura: 0.7 }, (pedaco) => {
      acumulado += pedaco;
      aoAndamento({ fase: "gerando", texto: acumulado });
    });

  let fim;
  try {
    fim = await gerar();
  } catch (e) {
    // O sistema pode ter tirado o modelo da memória ENTRE a carga e a geração —
    // são dois instantes diferentes, e num aparelho apertado cabe um despejo no
    // meio. Quando isso acontece, a tela mostrava "nenhum modelo carregado" e
    // parava, obrigando a pessoa a pedir de novo uma coisa que o aplicativo
    // sabia refazer sozinho.
    const motivo = e instanceof Error ? e.message : String(e);
    if (!/nenhum modelo carregado|modelo não carregado/i.test(motivo)) throw e;
    aoAndamento({ fase: "carregando", texto: "" });
    await llmNativo.carregar(arquivo);
    aoAndamento({ fase: "lendo", texto: "" });
    acumulado = "";
    // Uma segunda tentativa, e só uma: se o despejo se repetir, insistir vira
    // laço num aparelho que já está sem memória, e aí o erro é a resposta certa.
    fim = await gerar();
  }
  // O texto do evento `fim` é a versão completa segundo o motor; o acumulado é o
  // que a tela mostrou. Preferir o do motor evita que um pedaço perdido no
  // caminho vire uma resposta truncada gravada para sempre no histórico.
  const texto = fim.texto || acumulado;
  return { texto, porSegundo: fim.porSegundo, prefill: fim.prefill };
}

/** A chave do estado gravado em disco: uma conversa, um modelo.
 *
 *  Os dois no nome porque o arquivo guarda a tabela de atenção, que é
 *  específica do modelo que a produziu — e o formato não carrega essa
 *  informação. Restaurar num modelo diferente devolveria resposta sem sentido,
 *  sem erro nenhum aparecendo. */
export function chaveDoEstado(contato: string, arquivo: string): string {
  return `${contato}--${arquivo}`.replace(/\.gguf$/i, "");
}

/** Ingere o histórico ANTES de a pessoa perguntar.
 *
 *  A ingestão é a metade cara da resposta, e ela não depende da pergunta: o
 *  histórico já está escrito quando a conversa abre. Fazê-la enquanto a pessoa
 *  lê a tela troca uma espera que ela vive por um trabalho que ela não vê.
 *
 *  Vale para a PRIMEIRA pergunta de cada conversa. Da segunda em diante o
 *  motor já reaproveita o que está na memória sozinho — medido no aparelho:
 *  228 de 244 tokens vieram prontos. É a primeira que paga o preço cheio, e é
 *  ela que isto adianta.
 *
 *  Silencioso quando falha: é uma aposta de que a pessoa vai perguntar. Se der
 *  errado, quem mostra o erro é a resposta de verdade, quando ela for pedida. */
/** A ingestão adiantada está ligada?
 *
 *  Ela quebrou o aparelho do dono do app na primeira vez que rodou fora dos
 *  testes, e ficou desligada até a causa aparecer: a função chamada anotava o
 *  prompt em vez de processá-lo, e o modelo respondia sem ter lido a conversa.
 *  Corrigido no núcleo e medido pela linha de comando, voltou a ligar.
 *
 *  O interruptor fica: um recurso que já derrubou o aplicativo uma vez merece
 *  um jeito de desligar sem esperar compilação. Quem decide se ele pode rodar
 *  neste aparelho, porém, é a ponte — ver `llmNativo.ingestaoDisponivel`. */
export let ingestaoAdiantadaLigada = true;

export function ligarIngestaoAdiantada(ligada: boolean): void {
  ingestaoAdiantadaLigada = ligada;
}

/** Até que tamanho de conversa vale ler ao abrir.
 *
 *  Medido no aparelho do dono do app, e da pior forma: uma conversa de um dia
 *  inteiro travou em "lendo a conversa…" por minutos a cada abertura, com o
 *  aparelho esquentando, e o Android matando o aplicativo no meio.
 *
 *  O recurso troca uma espera que a pessoa vive por um trabalho que ela não vê.
 *  Isso vale enquanto o trabalho é curto. Numa conversa longa ele deixa de ser
 *  presente e vira imposto cobrado toda vez que ela entra — inclusive quando
 *  ela só quer reler o que já está escrito.
 *
 *  Seis mil caracteres são uns mil e quinhentos tokens: alguns segundos de
 *  trabalho no aparelho, não minutos. Acima disso a leitura acontece na hora da
 *  pergunta, onde a pessoa ao menos está esperando algo que pediu. */
export const TETO_DA_INGESTAO = 6000;

/** Quanto tempo uma leitura de disco honesta pode levar, em milissegundos.
 *
 *  Um estado de 14 MB sai do disco em menos de dois segundos no aparelho. Se a
 *  faixa continuar dizendo "carregando do disco" depois disso, ela está
 *  descrevendo outra coisa — e foi o que aconteceu: dois minutos com a frase do
 *  disco na tela, enquanto o que rodava era a leitura da conversa. */
const TEMPO_DE_UMA_LEITURA_DE_DISCO = 2500;

/** Espera em curso, em palavras curtas: "8s", "1min 20s".
 *
 *  Sem casas decimais e sem centésimos: quem espera quer saber a ordem de
 *  grandeza, não a precisão. */
export function relogio(ms: number): string {
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}min ${String(s % 60).padStart(2, "0")}s`;
}

export function cabeNaIngestaoAdiantada(historico: Array<{ text?: string }>): boolean {
  let total = 0;
  for (const m of historico) {
    total += (m.text || "").length + 12;
    if (total > TETO_DA_INGESTAO) return false;
  }
  return true;
}

/** Por que a última ingestão adiantada não aconteceu. Vazio quando aconteceu. */
export let motivoDaRecusa = "";

function recusa(motivo: string): null {
  motivoDaRecusa = motivo;
  return null;
}

export async function preIngerir(
  contato: string,
  arquivo: string,
  historico: ChatMessage[],
  aoComecar: () => void = () => {},
  aoRestaurar: (restaurados: number) => void = () => {},
): Promise<llmNativo.FimDaIngestao | null> {
  // Cada recusa diz o seu nome.
  //
  // A primeira versão devolvia `null` em quatro situações diferentes, e na tela
  // as quatro eram a mesma coisa: nada acontecia. Quando a faixa não apareceu
  // no aparelho do dono do app, não havia como saber se o recurso estava
  // desligado, se o aplicativo era antigo, se o modelo não tinha carregado ou
  // se a conversa estava vazia — e cada uma dessas pede uma ação diferente.
  if (!ingestaoAdiantadaLigada) return recusa("desligada pelo interruptor");
  // O teto vem antes de qualquer trabalho: numa conversa grande, o que vem
  // depois é minutos de processamento que ninguém pediu.
  if (!cabeNaIngestaoAdiantada(historico)) {
    return recusa("conversa grande demais para ler ao abrir; será lida na pergunta");
  }
  if (!llmNativo.disponivel()) return recusa("não há motor neste aparelho");
  // A trava de versão. Sem ela, um aparelho com o APK antigo receberia a tela
  // nova e voltaria a responder sem ter lido a conversa.
  if (!llmNativo.ingestaoDisponivel()) {
    return recusa("o aplicativo instalado não sabe ingerir; atualize o APK");
  }
  if (cargaEmCurso) await esperarCarga();
  if (modeloNaMemoria() !== arquivo) {
    return recusa(`o modelo ${arquivo} não está na memória (está: ${modeloNaMemoria() ?? "nenhum"})`);
  }
  motivoDaRecusa = "";
  try {
    return await llmNativo.ingerir(
      historicoParaPrompt(historico),
      chaveDoEstado(contato, arquivo),
      aoComecar,
      aoRestaurar,
    );
  } catch (e) {
    return recusa(e instanceof Error ? e.message : String(e));
  }
}

/** Interrompe a geração em curso. */
export function parar(): void {
  llmNativo.parar();
}

/** Carga já em andamento, para dois pedidos não virarem duas cargas. */
let cargaEmCurso: Promise<void> | null = null;

/**
 * Começa a carregar o modelo AGORA, sem esperar a mensagem.
 *
 * A carga leva dezenas de segundos e, até aqui, começava no envio — então a
 * pessoa escrevia a frase inteira com o motor parado e só então esperava,
 * olhando uma tela que não se move. Chamado quando ela escolhe o modelo (no
 * seletor do `@`) ou abre a conversa com ele, a mesma espera acontece enquanto
 * ela digita, e o enviar encontra tudo pronto.
 *
 * O tempo total não muda. O tempo VISÍVEL é o que muda, e é o único que a
 * pessoa vive.
 *
 * Silencioso de propósito: é uma aposta de que ela vai usar. Se falhar, quem
 * mostra o erro é a geração de verdade, quando ela realmente pedir.
 */
export function preAquecer(arquivo: string): void {
  if (!llmNativo.disponivel()) return;
  if (modeloNaMemoria() === arquivo || cargaEmCurso) return;
  if (modeloNaMemoria()) llmNativo.descarregar();
  cargaEmCurso = llmNativo
    .carregar(arquivo)
    .then(() => {})
    .catch(() => {
      // Silencioso: é uma aposta de que vai ser usado. Quem mostra o erro é a
      // geração de verdade, quando ela for pedida.
    })
    .finally(() => {
      cargaEmCurso = null;
    });
}

/** Espera a pré-carga terminar, se houver uma em andamento. */
export async function esperarCarga(): Promise<void> {
  if (cargaEmCurso) await cargaEmCurso;
}

/** Ponte para os testes, no mesmo padrão dos outros módulos.
 *
 *  A montagem do prompt é função pura e é onde mora a regra de quanto histórico
 *  vai — expor isso permite testá-la sem modelo, sem aparelho e sem encenação. */
declare global {
  interface Window {
    __wuweiIa: {
      ligarIngestaoAdiantada: typeof ligarIngestaoAdiantada;
      historicoParaPrompt: typeof historicoParaPrompt;
      historicoParaResumo: typeof historicoParaResumo;
      cabeNaIngestaoAdiantada: typeof cabeNaIngestaoAdiantada;
      responder: typeof responder;
      textoDaFase: typeof textoDaFase;
      relogio: typeof relogio;
      TETO_DA_INGESTAO: typeof TETO_DA_INGESTAO;
    };
  }
}

if (typeof window !== "undefined") {
  window.__wuweiIa = {
    historicoParaPrompt,
    historicoParaResumo,
    ligarIngestaoAdiantada,
    cabeNaIngestaoAdiantada,
    responder,
    textoDaFase,
    relogio,
    TETO_DA_INGESTAO,
  };
}
